'use memo'

import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  AgxPropertyAddressForm,
  PropertyAddress,
  SuburbSuggestions,
} from '@urbanx/agx-ui-components'
import {
  DevCampaignScenarioCreateValues,
  DevCampaignScenarioCreatedCampaign,
  DevCampaignScenarioDialog,
  type DropdownOption,
} from '@urbanx/agx.ui-components2'
import { AuthContext } from '@urbanx/agx.auth.client'
import { useParams } from 'react-router-dom'
import { getSuburbs } from 'services/functions/rex/getSuburbs'
import { useGetAllAgencies } from '@urbanx/agx.agencies.client'
import {
  ActivitiesApiContext,
  TaskProgressorRequestMode,
  TaskProgressorRequestTargetStage,
  useProgressTasksWithTaskProgressor,
} from '@urbanx/agx.activities.client'
import {
  type AustralianAddress,
  DevCreateContractTestCampaignRequestContractCreationSource,
  DevCreateContractTestCampaignRequestContractStage,
  DevCreateContractTestCampaignRequestCreateFrom,
  DevCreateContractTestCampaignRequestState,
  useDevCreateContractTestCampaign,
} from '@urbanx/agx.campaigns.client'

interface DevCampaignScenarioModalProps {
  onClose: () => void
}

interface ManualAddressInputProps {
  state: DevCreateContractTestCampaignRequestState
  disabled: boolean
  value: AustralianAddress
  onChange: (value: AustralianAddress) => void
  onValidityChange: (isValid: boolean) => void
}

const getStaffPortalCampaignUrl = ({
  campaignId,
  agencyId,
}: DevCampaignScenarioCreatedCampaign) =>
  `${window.location.origin}/agencies/${agencyId}/campaigns/${campaignId}/campaign-tasks`

const ManualAddressInput = ({
  state,
  disabled,
  value,
  onChange,
  onValidityChange,
}: ManualAddressInputProps) => {
  const { getAuthToken } = useContext(AuthContext)
  const [suburbSuggestions, setSuburbSuggestions] = useState<
    SuburbSuggestions[]
  >([])

  const getSuburbSuggestions = useCallback(
    async (query: string) => {
      if (query.length < 3) {
        setSuburbSuggestions([])
        return
      }

      const authToken = await getAuthToken?.()
      if (!authToken) return

      const controller = new AbortController()
      const result = await getSuburbs(
        authToken,
        query,
        state,
        controller.signal
      )

      setSuburbSuggestions(
        result.map((suggestion) => ({
          state: suggestion.stateOrRegion,
          postcode: suggestion.postCode,
          suggestionLine1: suggestion.suburbOrTown,
          suggestionLine2: `${suggestion.stateOrRegion} ${suggestion.postCode}`,
        }))
      )
    },
    [getAuthToken, state]
  )

  return (
    <AgxPropertyAddressForm
      key={`dev-campaign-scenario-manual-address-${state}`}
      id="dev-campaign-scenario-manual-address"
      searchSuburb
      hideUnitLot
      showSearchForAddress={false}
      propertyAddressField={false}
      suburbSuggestions={suburbSuggestions}
      onFetchSuburbSuggestions={(suburbTerm) =>
        getSuburbSuggestions(suburbTerm || '')
      }
      onClearSuburbSuggestions={() => setSuburbSuggestions([])}
      onDisableNextButton={(disabled) => onValidityChange(!disabled)}
      disabled={disabled}
      required
      defaultValue={value as PropertyAddress}
      onAddressSelected={(selectedAddress) => {
        if (selectedAddress) onChange(selectedAddress)
      }}
    />
  )
}

export const DevCampaignScenarioModal = ({
  onClose,
}: DevCampaignScenarioModalProps) => {
  const { userAccount } = useContext(AuthContext)
  const activitiesApi = useContext(ActivitiesApiContext)
  const { agencyId } = useParams()
  const [selectedAgencyId, setSelectedAgencyId] = useState(
    agencyId ?? userAccount?.agency ?? ''
  )
  const { data: agencies, isPending: isLoadingAgencies } = useGetAllAgencies()
  const { devCreateContractTestCampaign } = useDevCreateContractTestCampaign({
    onSuccess: () => {},
    onError: () => {},
  })
  const { progressTasksWithTaskProgressor } =
    useProgressTasksWithTaskProgressor({
      onSuccess: () => {},
      onError: () => {},
    })

  useEffect(() => {
    setSelectedAgencyId(agencyId ?? userAccount?.agency ?? '')
  }, [agencyId, userAccount?.agency])

  const agencyOptions = useMemo<DropdownOption[]>(
    () =>
      (agencies ?? [])
        .filter((agency) => !!agency.id)
        .map((agency) => ({
          label: agency.name ?? 'Unnamed agency',
          value: agency.id!,
          searchText: agency.name ?? '',
        })),
    [agencies]
  )

  const agencyNotice =
    selectedAgencyId &&
    userAccount?.agency &&
    selectedAgencyId !== userAccount.agency
      ? 'Different to your Agent Portal agency. Check before creating.'
      : undefined

  const handleCreate = useCallback(
    async (values: DevCampaignScenarioCreateValues) => {
      if (!values.agencyId) {
        throw new Error('Select an agency before creating a test campaign.')
      }

      return await devCreateContractTestCampaign({
        createFrom:
          DevCreateContractTestCampaignRequestCreateFrom.ManualAddress,
        agencyId: values.agencyId,
        state: values.state,
        propertyAddress: {
          ...values.manualAddress,
          state: values.manualAddress.state || values.state,
        } as PropertyAddress,
        agreementSource: values.agreementSource,
        agreementDocuSignSigned: values.agreementDocuSignSigned,
        listingLaunch: values.listingLaunch,
        contract: values.contract,
        contractCreationSource:
          DevCreateContractTestCampaignRequestContractCreationSource.AgentPortal,
        contractConditions: values.conditions,
        contractStage: values.contractStage,
        depositProfile: values.depositProfile,
      })
    },
    [devCreateContractTestCampaign]
  )

  const handleProgressContractWorkflow = useCallback(
    async (
      campaign: DevCampaignScenarioCreatedCampaign,
      status: DevCreateContractTestCampaignRequestContractStage
    ) => {
      const progressWorkflow = async (
        targetStage: TaskProgressorRequestTargetStage,
        contractId?: string
      ) => {
        const details = await activitiesApi.getTaskProgressorCampaignDetails(
          campaign.campaignId
        )

        if (!details.agencyId || !details.campaignVersion) {
          throw new Error(
            'Task progressor returned incomplete campaign details.'
          )
        }

        await progressTasksWithTaskProgressor({
          agencyId: details.agencyId,
          campaignId: campaign.campaignId,
          contractId,
          targetStage,
          mode: TaskProgressorRequestMode.ChangeStage,
          campaignVersion: details.campaignVersion,
        })
      }

      await progressWorkflow(TaskProgressorRequestTargetStage.Launch)

      if (!campaign.contractId) return

      switch (status) {
        case DevCreateContractTestCampaignRequestContractStage.NewContract:
          return
        case DevCreateContractTestCampaignRequestContractStage.Accepted:
          await progressWorkflow(
            TaskProgressorRequestTargetStage.NewContract,
            campaign.contractId
          )
          return
        case DevCreateContractTestCampaignRequestContractStage.Unconditional:
          await progressWorkflow(
            TaskProgressorRequestTargetStage.Unconditional,
            campaign.contractId
          )
      }
    },
    [activitiesApi, progressTasksWithTaskProgressor]
  )

  return (
    <DevCampaignScenarioDialog
      allowRpDataSearch={false}
      agencyOptions={agencyOptions}
      selectedAgencyId={selectedAgencyId}
      onAgencyChange={setSelectedAgencyId}
      isLoadingAgencies={isLoadingAgencies}
      agencyNotice={agencyNotice}
      onClose={onClose}
      onCreate={handleCreate}
      onProgressContractWorkflow={handleProgressContractWorkflow}
      getStaffPortalUrl={getStaffPortalCampaignUrl}
      renderManualAddressForm={(props) => <ManualAddressInput {...props} />}
    />
  )
}

export default DevCampaignScenarioModal
